import { useEffect, useRef } from 'react';
import config from '@/config.window';
import { isReachable } from '@/hooks/use-online';

/**
 * On boot and whenever the app regains connectivity/foreground, compares the
 * build identifier baked into this bundle (config.build.buildNumber) against
 * the `/build` file the pipeline writes at deploy time. A mismatch means a
 * newer version is live: trigger the service worker's own update check
 * (its precache manifest — and therefore its byte content — differs on every
 * build, so the browser's SW update diff reliably fires) and reload once the
 * new worker takes control. Never runs while offline, and a failed/timed-out
 * fetch is treated the same as "nothing to do" — an offline session is never
 * disrupted by this check.
 *
 * `navigator.onLine` only reflects whether the device has a network
 * interface up — a captive portal, a VPN that's connected but not routing,
 * or plain DNS trouble all report `true` there while nothing actually
 * resolves. Bail out fast on `navigator.onLine === false` (a cheap, reliable
 * true-negative), but treat it as necessary and not sufficient: before doing
 * anything else, confirm the GraphQL host (`config.build.host`, the same
 * app-critical dependency `useIsOnline` checks) is actually reachable. Only
 * then is a `/build` mismatch trusted enough to bust the shell cache and
 * reload — a false "online" reading must never trigger that.
 */
export function useBuildVersionCheck() {
	const checking = useRef(false);

	useEffect(() => {
		const checkVersion = async () => {
			if (checking.current || !navigator.onLine) {
				return;
			}
			checking.current = true;
			try {
				const online = await isReachable(config.build.host).then(
					() => true,
					() => false,
				);
				if (!online) {
					return;
				}

				const url = `${import.meta.env.BASE_URL}build?t=${Date.now()}`;
				const response = await fetch(url, { cache: 'no-store' });
				if (!response.ok) {
					return;
				}
				const remoteBuildNumber = (await response.text()).trim();
				const currentBuildNumber = String(config.build.buildNumber);
				if (remoteBuildNumber && remoteBuildNumber !== currentBuildNumber) {
					console.info(
						`New build detected (${currentBuildNumber} -> ${remoteBuildNumber}), applying update`,
					);
					await applyUpdate();
				}
			} catch {
				// Offline or the request failed — no-op, this check is purely additive.
			} finally {
				checking.current = false;
			}
		};

		checkVersion();

		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				checkVersion();
			}
		};

		window.addEventListener('online', checkVersion);
		document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			window.removeEventListener('online', checkVersion);
			document.removeEventListener(
				'visibilitychange',
				onVisibilityChange,
			);
		};
	}, []);
}

async function applyUpdate() {
	if (!('serviceWorker' in navigator)) {
		window.location.reload();
		return;
	}

	try {
		const registration = await navigator.serviceWorker.getRegistration();
		if (!registration) {
			window.location.reload();
			return;
		}

		const reloadOnce = (() => {
			let reloaded = false;
			return () => {
				if (reloaded) {
					return;
				}
				reloaded = true;
				window.location.reload();
			};
		})();

		navigator.serviceWorker.addEventListener('controllerchange', reloadOnce, {
			once: true,
		});

		// Belt-and-suspenders fallback for when controllerchange never fires
		// (e.g. the update check finds nothing new to install). Only arm it
		// once we know a new worker actually reached the "installed" state —
		// starting a fixed timer immediately would reload the page while a
		// new worker is still precaching the app shell on a slow connection,
		// before it can ever take control, producing a reload loop.
		const armFallbackReload = (worker: ServiceWorker) => {
			const maybeSchedule = () => {
				if (worker.state === 'installed' || worker.state === 'activated') {
					setTimeout(reloadOnce, 3000);
				}
				// 'redundant' means install failed — nothing to reload for.
			};
			worker.addEventListener('statechange', maybeSchedule);
			maybeSchedule();
		};

		if (registration.installing) {
			armFallbackReload(registration.installing);
		} else {
			registration.addEventListener(
				'updatefound',
				() => {
					if (registration.installing) {
						armFallbackReload(registration.installing);
					}
				},
				{ once: true },
			);
		}

		await registration.update();
	} catch {
		window.location.reload();
	}
}
